import type { DocumentType } from '@/types/pdfAnalysis';

/**
 * PDF解析用のプロンプトを生成するクラス
 */
export class PDFPromptBuilder {
  /**
   * ドキュメントタイプに応じたプロンプトを生成
   */
  buildPrompt(documentType: DocumentType): string {
    switch (documentType) {
      case 'auto':
        return this.buildUnifiedPrompt();
      case 'pedigree':
        return this.buildPedigreePrompt();
      case 'microchip':
        return this.buildMicrochipPrompt();
      case 'geneticTest':
        return this.buildGeneticTestPrompt();
      default:
        throw new Error(`不明なドキュメントタイプ: ${documentType}`);
    }
  }

  /**
   * 自動検出用の統合プロンプトを生成
   * 1回のAPI呼び出しでドキュメントタイプ判定と情報抽出を同時実行
   */
  buildUnifiedPrompt(): string {
    return `あなたは犬関連のドキュメントを解析するエキスパートです。

## タスク
1. まず、アップロードされた画像が以下のどの種類のドキュメントか判定してください：
   - pedigree: 血統書（JKCなどの犬種登録団体が発行する血統証明書）
   - microchip: マイクロチップ証明書（マイクロチップの登録証明書）
   - geneticTest: 遺伝子検査結果（遺伝性疾患の検査結果報告書）

2. 判定したドキュメントタイプに応じて、適切な情報を抽出してください。

---

## ドキュメントタイプ別の抽出情報

### 血統書（pedigree）の場合
以下の情報を抽出:
- **officialName**: 正式名称（血統書名）- 犬の正式な登録名
- **breed**: 犬種 - 例: トイプードル、チワワ、柴犬など
- **jkcRegistrationNumber**: JKC登録番号
- **dnaRegistrationNumber**: DNA登録番号
- **gender**: 性別 - "male" または "female"（牡/牝の表記から判断）
- **birthDate**: 生年月日 - YYYY-MM-DD形式
- **color**: 毛色
- **breederName**: 繁殖者名
- **ownerName**: 所有者名 - 血統書に記載の所有者（繁殖者とは別欄）

血統情報（ancestors）:
- **father**: 父犬の名前と登録番号
- **mother**: 母犬の名前と登録番号
- **paternalGrandfather**: 父方祖父の名前と登録番号
- **paternalGrandmother**: 父方祖母の名前と登録番号
- **maternalGrandfather**: 母方祖父の名前と登録番号
- **maternalGrandmother**: 母方祖母の名前と登録番号

### マイクロチップ証明書（microchip）の場合
以下の情報を抽出:
- **microchipNumber**: マイクロチップ番号 - 15桁の数字（ISO規格、先頭392は日本の国コード）
- **registrationDate**: 登録日 - YYYY-MM-DD形式
- **pin**: 暗証記号 - マイクロチップの暗証記号（PIN）

### 遺伝子検査結果（geneticTest）の場合
以下の情報を抽出:
- **animalName**: どうぶつ名（犬の名前）- 検査報告書に記載されている犬の名前
- **testResults**: 検査結果の配列
  - **testName**: 検査項目名（例: PRA、PLL、DM、vWDなど）
  - **result**: "clear" | "carrier" | "affected"
    - Clear/クリア/N/N/-/- → "clear"
    - Carrier/キャリア/N/A/+/- → "carrier"
    - Affected/アフェクテッド/A/A/+/+ → "affected"
- **testDate**: 検査日 - YYYY-MM-DD形式
- **laboratory**: 検査機関名

---

## 出力形式
必ず以下のJSON形式で出力してください。

\`\`\`json
{
  "documentType": "pedigree" | "microchip" | "geneticTest",
  "data": {
    // ドキュメントタイプに応じたデータ
    // pedigreeの場合: officialName, breed, jkcRegistrationNumber, gender, birthDate, color, breederName, ownerName, ancestors
    // microchipの場合: microchipNumber, registrationDate, pin
    // geneticTestの場合: animalName, testResults, testDate, laboratory
  },
  "confidence": 0-100,
  "warnings": ["認識精度が低い項目についての警告"]
}
\`\`\`

## 重要な注意事項
- まずドキュメントの種類を正確に判定してください
- 読み取れない項目はnullとしてください
- 日付は西暦のYYYY-MM-DD形式に変換
- confidenceは全体的な認識精度を0-100で評価
- 判定に迷う場合や読み取りが困難な場合はwarningsに記載`;
  }

  /**
   * 血統書解析用プロンプト
   */
  private buildPedigreePrompt(): string {
    return `あなたは犬の血統書を解析するエキスパートです。
添付された画像から以下の情報を正確に抽出してください。

## 抽出する情報
1. **officialName**: 正式名称（血統書名）- 犬の正式な登録名
2. **breed**: 犬種 - 例: トイプードル、チワワ、柴犬など
3. **jkcRegistrationNumber**: JKC登録番号 - JKCの登録番号（例: JKC-12345）
4. **dnaRegistrationNumber**: DNA登録番号 - DNA検査の登録番号
5. **gender**: 性別 - "male" または "female"（牡/牝の表記から判断）
6. **birthDate**: 生年月日 - YYYY-MM-DD形式
7. **color**: 毛色
8. **breederName**: 繁殖者名
9. **ownerName**: 所有者名 - 血統書に記載の所有者（繁殖者とは別欄）

## 血統情報（ancestors）
- **father**: 父犬の名前と登録番号
- **mother**: 母犬の名前と登録番号
- **paternalGrandfather**: 父方祖父の名前と登録番号
- **paternalGrandmother**: 父方祖母の名前と登録番号
- **maternalGrandfather**: 母方祖父の名前と登録番号
- **maternalGrandmother**: 母方祖母の名前と登録番号

## 出力形式
必ず以下のJSON形式で出力してください。読み取れない項目はnullとしてください。

\`\`\`json
{
  "officialName": "string or null",
  "breed": "string or null",
  "jkcRegistrationNumber": "string or null",
  "dnaRegistrationNumber": "string or null",
  "gender": "male" | "female" | null,
  "birthDate": "YYYY-MM-DD or null",
  "color": "string or null",
  "breederName": "string or null",
  "ownerName": "string or null",
  "ancestors": {
    "father": { "name": "string or null", "registrationNumber": "string or null" },
    "mother": { "name": "string or null", "registrationNumber": "string or null" },
    "paternalGrandfather": { "name": "string or null", "registrationNumber": "string or null" },
    "paternalGrandmother": { "name": "string or null", "registrationNumber": "string or null" },
    "maternalGrandfather": { "name": "string or null", "registrationNumber": "string or null" },
    "maternalGrandmother": { "name": "string or null", "registrationNumber": "string or null" }
  },
  "confidence": 0-100,
  "warnings": ["認識精度が低い項目についての警告"]
}
\`\`\`

## 重要な注意事項
- JKCの血統書フォーマットを基準にしてください
- 牡（オス）= "male"、牝（メス）= "female" として出力
- 日付は西暦のYYYY-MM-DD形式に変換
- 読み取りが困難な場合はnullとし、warningsに記載
- confidenceは全体的な認識精度を0-100で評価`;
  }

  /**
   * マイクロチップ証明書解析用プロンプト
   */
  private buildMicrochipPrompt(): string {
    return `あなたはマイクロチップ登録証明書を解析するエキスパートです。
添付された画像から以下の情報を正確に抽出してください。

## 抽出する情報
1. **microchipNumber**: マイクロチップ番号 - 15桁の数字（ISO規格）
2. **registrationDate**: 登録日 - YYYY-MM-DD形式
3. **pin**: 暗証記号 - マイクロチップの暗証記号（PIN）

## マイクロチップ番号について
- 日本では15桁の数字（例: 392141234567890）
- 先頭の392は日本の国コード
- 番号は明確に表示されているはずです

## 出力形式
必ず以下のJSON形式で出力してください。

\`\`\`json
{
  "microchipNumber": "15桁の数字",
  "registrationDate": "YYYY-MM-DD or null",
  "pin": "string or null",
  "confidence": 0-100,
  "warnings": ["認識精度が低い項目についての警告"]
}
\`\`\`

## 重要な注意事項
- マイクロチップ番号は正確に読み取ってください（15桁）
- 数字の誤認識に注意（0とO、1とIなど）
- 読み取りが困難な場合はwarningsに記載`;
  }

  /**
   * 遺伝子検査結果解析用プロンプト
   */
  private buildGeneticTestPrompt(): string {
    return `あなたは犬の遺伝子検査結果報告書を解析するエキスパートです。
添付された画像から以下の情報を正確に抽出してください。

## 抽出する情報

### どうぶつ名
- **animalName**: どうぶつ名（犬の名前）- 検査報告書に記載されている犬の名前

### 検査結果（testResults）
各検査項目について以下の情報を抽出:
- **testName**: 検査項目名（例: PRA、PLL、DM、vWDなど）
- **result**: 結果 - "clear"（クリア）、"carrier"（キャリア）、"affected"（アフェクテッド）

### その他
- **testDate**: 検査日 - YYYY-MM-DD形式
- **laboratory**: 検査機関名

## 一般的な遺伝子検査項目
- **PRA** (Progressive Retinal Atrophy): 進行性網膜萎縮症
- **PLL** (Primary Lens Luxation): 原発性水晶体脱臼
- **DM** (Degenerative Myelopathy): 変性性脊髄症
- **vWD** (von Willebrand Disease): フォン・ヴィレブランド病
- **MDR1**: 薬剤感受性
- **NCL**: 神経セロイドリポフスチン症
- **GM1/GM2**: ガングリオシドーシス

## 結果の判定基準
- **Clear / クリア / N/N / -/-**: "clear"
- **Carrier / キャリア / N/A / +/-**: "carrier"
- **Affected / アフェクテッド / A/A / +/+**: "affected"

## 出力形式
必ず以下のJSON形式で出力してください。

\`\`\`json
{
  "animalName": "犬の名前 or null",
  "testResults": [
    { "testName": "PRA", "result": "clear" },
    { "testName": "PLL", "result": "carrier" }
  ],
  "testDate": "YYYY-MM-DD or null",
  "laboratory": "検査機関名 or null",
  "confidence": 0-100,
  "warnings": ["認識精度が低い項目についての警告"]
}
\`\`\`

## 重要な注意事項
- GENETICHECK、LABOKLIN、Embarkなど各検査機関のフォーマットに対応
- 検査項目名は略称（PRA）と正式名称のどちらでも認識
- 結果の表記ゆれ（Clear/クリア/N/Nなど）を統一形式に変換
- 読み取りが困難な項目はwarningsに記載`;
  }
}

// シングルトンインスタンスをエクスポート
export const pdfPromptBuilder = new PDFPromptBuilder();
